let Cesium: any = null;
try {
  Cesium = require('cesium');
} catch {
  try {
    Cesium = (globalThis as any).Cesium || null;
  } catch {
    Cesium = null;
  }
}

const DEFAULT_API_BASE = '';

async function apiCall(endpoint: string, body: any, baseUrl?: string): Promise<any> {
  const base = baseUrl || MISpatialAnalysis3D.apiBaseUrl || DEFAULT_API_BASE;
  const url = `${base}/api/v2${endpoint}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Server error' }));
    throw new Error(err.error || `API call failed: ${response.status}`);
  }
  return response.json();
}

export interface MIViewshedOptions {
  observer: [number, number, number];
  observerHeightAboveGround?: number;
  heading?: number;
  horizontalFOV?: number;
  verticalFOV?: number;
  minDistance?: number;
  maxDistance?: number;
  resolution?: number;
  visibleColor?: string;
  hiddenColor?: string;
  analysisSurface?: 'terrain' | '3dtiles' | 'both';
  interactive?: boolean;
}

export interface MIViewshedResult {
  visibleArea: number;
  hiddenArea: number;
  visiblePercentage: number;
  visiblePolygon: any;
  hiddenPolygon: any;
  observerPosition: [number, number, number];
  remove(): void;
  update(options: Partial<MIViewshedOptions>): void;
  setObserver(position: [number, number, number]): void;
  on(event: string, callback: Function): void;
}

export interface MILineOfSightOptions {
  observer: [number, number, number];
  targets: [number, number, number][];
  observerHeightAboveGround?: number;
  targetHeightAboveGround?: number;
  analysisSurface?: 'terrain' | '3dtiles' | 'both';
  visibleLineColor?: string;
  hiddenLineColor?: string;
  obstructionPointColor?: string;
  lineWidth?: number;
  interactive?: boolean;
}

export interface MILineOfSightResult {
  sightLines: MISightLine[];
  remove(): void;
  update(options: Partial<MILineOfSightOptions>): void;
  addTarget(target: [number, number, number]): void;
  removeTarget(index: number): void;
}

export interface MISightLine {
  target: [number, number, number];
  isVisible: boolean;
  distance: number;
  obstructions: MIObstructionPoint[];
}

export interface MIObstructionPoint {
  position: [number, number, number];
  distance: number;
  type: 'terrain' | '3dtile';
}

export interface MIMeasure3DOptions {
  type: 'distance' | 'area' | 'volume' | 'height' | 'slope-distance' | 'elevation-profile';
  unit?: 'meters' | 'kilometers' | 'feet' | 'miles' | 'nautical-miles';
  areaUnit?: 'square-meters' | 'hectares' | 'acres' | 'square-kilometers';
  volumeUnit?: 'cubic-meters' | 'cubic-feet';
  snappingEnabled?: boolean;
  followTerrain?: boolean;
  labelStyle?: any;
  lineStyle?: any;
}

export interface MIMeasureHandle {
  result: MIMeasureResult;
  on(event: string, callback: Function): void;
  clear(): void;
  remove(): void;
}

export interface MIMeasureResult {
  type: string;
  value: number;
  unit: string;
  points: [number, number, number][];
  directDistance?: number;
  surfaceDistance?: number;
  horizontalDistance?: number;
  verticalDistance?: number;
  area?: number;
  perimeter?: number;
  volume?: number;
  cutVolume?: number;
  fillVolume?: number;
  slope?: number;
  elevationProfile?: { distance: number; elevation: number }[];
}

export interface MIShadowAnalysisOptions {
  type: 'instant' | 'duration' | 'animation';
  date?: Date;
  timeRange?: { start: Date; end: Date };
  timeStep?: number;
  analysisExtent?: any;
  surfaceResolution?: number;
  includeBuildings?: boolean;
  outputType?: 'visual' | 'statistics' | 'both';
}

export interface MIShadowAnalysisResult {
  type: string;
  shadowMap?: HTMLCanvasElement;
  statistics?: {
    totalArea: number;
    shadowedArea: number;
    litArea: number;
    shadowPercentage: number;
    hoursOfSunlight?: Map<string, number>;
    minimumSunlightHours?: number;
    maximumSunlightHours?: number;
    averageSunlightHours?: number;
  };
  animationHandle?: any;
  remove(): void;
}

export interface MICrossSectionOptions {
  line: [number, number, number][];
  width?: number;
  surfaces: ('terrain' | '3dtiles' | 'pointcloud')[];
  sampleResolution?: number;
  fillColor?: string;
  lineColor?: string;
  interactive?: boolean;
}

export interface MICrossSectionResult {
  profiles: {
    surface: string;
    points: { distance: number; elevation: number; coordinate: [number, number, number] }[];
  }[];
  totalLength: number;
  elevationRange: [number, number];
  chart?: HTMLCanvasElement;
  remove(): void;
  update(line: [number, number, number][]): void;
}

const EARTH_RADIUS_M = 6371008.8;
const DEG_TO_RAD = Math.PI / 180;

function haversineDistance(
  lon1: number, lat1: number, alt1: number,
  lon2: number, lat2: number, alt2: number
): { horizontal: number; vertical: number; direct: number } {
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLon = (lon2 - lon1) * DEG_TO_RAD;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const horizontal = EARTH_RADIUS_M * c;
  const vertical = alt2 - alt1;
  const direct = Math.sqrt(horizontal * horizontal + vertical * vertical);
  return { horizontal, vertical, direct };
}

function convertDistance(meters: number, unit: string): number {
  switch (unit) {
    case 'kilometers': return meters / 1000;
    case 'feet': return meters * 3.28084;
    case 'miles': return meters / 1609.344;
    case 'nautical-miles': return meters / 1852;
    default: return meters;
  }
}

function convertArea(sqMeters: number, unit: string): number {
  switch (unit) {
    case 'hectares': return sqMeters / 10000;
    case 'acres': return sqMeters / 4046.856;
    case 'square-kilometers': return sqMeters / 1000000;
    default: return sqMeters;
  }
}

function convertVolume(cubicMeters: number, unit: string): number {
  switch (unit) {
    case 'cubic-feet': return cubicMeters * 35.3147;
    default: return cubicMeters;
  }
}

function computeSunPosition(date: Date, lat: number, lon: number): { azimuth: number; elevation: number } {
  const dayOfYear = Math.floor(
    (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86400000
  );
  const declination = -23.45 * Math.cos((360 / 365) * (dayOfYear + 10) * DEG_TO_RAD);

  const hours = date.getUTCHours() + date.getUTCMinutes() / 60;
  const solarTime = hours + lon / 15;
  const hourAngle = (solarTime - 12) * 15;

  const latRad = lat * DEG_TO_RAD;
  const decRad = declination * DEG_TO_RAD;
  const haRad = hourAngle * DEG_TO_RAD;

  const sinElev =
    Math.sin(latRad) * Math.sin(decRad) +
    Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);
  const elevation = Math.asin(Math.max(-1, Math.min(1, sinElev))) / DEG_TO_RAD;

  const cosAz =
    (Math.sin(decRad) - Math.sin(latRad) * sinElev) /
    (Math.cos(latRad) * Math.cos(elevation * DEG_TO_RAD));
  let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) / DEG_TO_RAD;
  if (hourAngle > 0) {
    azimuth = 360 - azimuth;
  }

  return { azimuth, elevation };
}

function getViewer(scene: any): any | null {
  if (!scene) return null;
  if (scene._viewer) return scene._viewer;
  if (scene._cesiumAvailable && scene._viewer) return scene._viewer;
  if (typeof scene.getViewer === 'function') {
    try { return scene.getViewer(); } catch { return null; }
  }
  return null;
}

function interpolatePosition(
  p1: [number, number, number],
  p2: [number, number, number],
  t: number
): [number, number, number] {
  return [
    p1[0] + (p2[0] - p1[0]) * t,
    p1[1] + (p2[1] - p1[1]) * t,
    p1[2] + (p2[2] - p1[2]) * t,
  ];
}

function sampleTerrainHeight(
  viewer: any,
  lon: number,
  lat: number
): number {
  if (!viewer || !Cesium) return 0;
  try {
    const scene = viewer.scene;
    const cartographic = Cesium.Cartographic.fromDegrees(lon, lat);
    const height = scene.globe.getHeight(cartographic);
    return height !== undefined && height !== null ? height : 0;
  } catch {
    return 0;
  }
}

export class MISpatialAnalysis3D {
  static apiBaseUrl: string = '';

  static viewshed(scene: any, options: MIViewshedOptions): MIViewshedResult {
    const viewer = getViewer(scene);
    const heading = options.heading ?? 0;
    const hFOV = options.horizontalFOV ?? 120;
    const vFOV = options.verticalFOV ?? 90;
    const minDist = options.minDistance ?? 0;
    const maxDist = options.maxDistance ?? 1000;
    const resolution = options.resolution ?? 36;
    const observerHeight = options.observerHeightAboveGround ?? 0;
    const observerPos: [number, number, number] = [
      options.observer[0],
      options.observer[1],
      options.observer[2] + observerHeight,
    ];

    let _removed = false;
    const _listeners: Map<string, Function[]> = new Map();
    let _currentOptions = { ...options };
    let _primitiveIds: any[] = [];

    const halfHFOV = hFOV / 2;
    const angleStep = hFOV / resolution;
    let visibleCount = 0;
    let hiddenCount = 0;
    const totalSamples = resolution * Math.max(1, Math.floor(vFOV / angleStep));
    const visiblePoints: [number, number, number][] = [];
    const hiddenPoints: [number, number, number][] = [];

    for (let i = 0; i < resolution; i++) {
      const azimuth = heading - halfHFOV + i * angleStep;
      const azRad = azimuth * DEG_TO_RAD;
      const sampleDist = maxDist;
      const dx = Math.sin(azRad) * sampleDist;
      const dy = Math.cos(azRad) * sampleDist;
      const targetLon = observerPos[0] + (dx / (EARTH_RADIUS_M * Math.cos(observerPos[1] * DEG_TO_RAD))) / DEG_TO_RAD;
      const targetLat = observerPos[1] + (dy / EARTH_RADIUS_M) / DEG_TO_RAD;

      let isVisible = true;

      if (viewer && Cesium) {
        try {
          const numSteps = 10;
          for (let s = 1; s <= numSteps; s++) {
            const t = s / numSteps;
            const sampleLon = observerPos[0] + (targetLon - observerPos[0]) * t;
            const sampleLat = observerPos[1] + (targetLat - observerPos[1]) * t;
            const terrainH = sampleTerrainHeight(viewer, sampleLon, sampleLat);
            const expectedH = observerPos[2] + (0 - observerPos[2]) * t;
            if (terrainH > expectedH) {
              isVisible = false;
              break;
            }
          }
        } catch {
          isVisible = true;
        }
      }

      const targetPos: [number, number, number] = [targetLon, targetLat, observerPos[2]];
      if (isVisible) {
        visibleCount++;
        visiblePoints.push(targetPos);
      } else {
        hiddenCount++;
        hiddenPoints.push(targetPos);
      }
    }

    const totalArea = Math.PI * maxDist * maxDist * (hFOV / 360);
    const visiblePercentage = totalSamples > 0 ? (visibleCount / totalSamples) * 100 : 0;
    const visibleArea = totalArea * (visiblePercentage / 100);
    const hiddenArea = totalArea - visibleArea;

    const visiblePolygon = visiblePoints.length > 2
      ? {
          type: 'Feature' as const,
          properties: { visibility: 'visible' },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [[
              observerPos.slice(0, 2),
              ...visiblePoints.map(p => p.slice(0, 2)),
              observerPos.slice(0, 2),
            ]],
          },
        }
      : null;

    const hiddenPolygon = hiddenPoints.length > 2
      ? {
          type: 'Feature' as const,
          properties: { visibility: 'hidden' },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [[
              observerPos.slice(0, 2),
              ...hiddenPoints.map(p => p.slice(0, 2)),
              observerPos.slice(0, 2),
            ]],
          },
        }
      : null;

    if (viewer && Cesium) {
      try {
        const visColor = options.visibleColor
          ? Cesium.Color.fromCssColorString(options.visibleColor).withAlpha(0.3)
          : Cesium.Color.GREEN.withAlpha(0.3);
        const hidColor = options.hiddenColor
          ? Cesium.Color.fromCssColorString(options.hiddenColor).withAlpha(0.3)
          : Cesium.Color.RED.withAlpha(0.3);

        if (visiblePolygon) {
          const entity = viewer.entities.add({
            polygon: {
              hierarchy: Cesium.Cartesian3.fromDegreesArray(
                (visiblePolygon.geometry.coordinates[0] as number[][]).flat()
              ),
              material: visColor,
              perPositionHeight: true,
            },
          });
          _primitiveIds.push(entity);
        }
        if (hiddenPolygon) {
          const entity = viewer.entities.add({
            polygon: {
              hierarchy: Cesium.Cartesian3.fromDegreesArray(
                (hiddenPolygon.geometry.coordinates[0] as number[][]).flat()
              ),
              material: hidColor,
              perPositionHeight: true,
            },
          });
          _primitiveIds.push(entity);
        }
      } catch {
      }
    }

    const result: MIViewshedResult = {
      visibleArea,
      hiddenArea,
      visiblePercentage,
      visiblePolygon,
      hiddenPolygon,
      observerPosition: [...observerPos],
      remove() {
        if (_removed) return;
        _removed = true;
        if (viewer) {
          for (const entity of _primitiveIds) {
            try { viewer.entities.remove(entity); } catch {}
          }
        }
        _primitiveIds = [];
      },
      update(updateOpts: Partial<MIViewshedOptions>) {
        result.remove();
        const merged = { ..._currentOptions, ...updateOpts };
        _currentOptions = merged;
        const newResult = MISpatialAnalysis3D.viewshed(scene, merged);
        Object.assign(result, newResult);
      },
      setObserver(position: [number, number, number]) {
        result.update({ observer: position });
      },
      on(event: string, callback: Function) {
        if (!_listeners.has(event)) _listeners.set(event, []);
        _listeners.get(event)!.push(callback);
      },
    };

    return result;
  }

  static lineOfSight(scene: any, options: MILineOfSightOptions): MILineOfSightResult {
    const viewer = getViewer(scene);
    const observerHeight = options.observerHeightAboveGround ?? 0;
    const targetHeight = options.targetHeightAboveGround ?? 0;
    const observer: [number, number, number] = [
      options.observer[0],
      options.observer[1],
      options.observer[2] + observerHeight,
    ];

    let _removed = false;
    let _currentOptions = { ...options };
    let _entities: any[] = [];

    const computeSightLines = (
      obs: [number, number, number],
      targets: [number, number, number][]
    ): MISightLine[] => {
      return targets.map((rawTarget) => {
        const target: [number, number, number] = [
          rawTarget[0],
          rawTarget[1],
          rawTarget[2] + targetHeight,
        ];

        const dist = haversineDistance(
          obs[0], obs[1], obs[2],
          target[0], target[1], target[2]
        );

        const obstructions: MIObstructionPoint[] = [];
        let isVisible = true;

        if (viewer && Cesium) {
          try {
            const numSamples = 20;
            for (let s = 1; s < numSamples; s++) {
              const t = s / numSamples;
              const sampleLon = obs[0] + (target[0] - obs[0]) * t;
              const sampleLat = obs[1] + (target[1] - obs[1]) * t;
              const lineHeight = obs[2] + (target[2] - obs[2]) * t;
              const terrainH = sampleTerrainHeight(viewer, sampleLon, sampleLat);

              if (terrainH > lineHeight) {
                isVisible = false;
                const obsDist = haversineDistance(
                  obs[0], obs[1], obs[2],
                  sampleLon, sampleLat, terrainH
                );
                obstructions.push({
                  position: [sampleLon, sampleLat, terrainH],
                  distance: obsDist.direct,
                  type: 'terrain',
                });
              }
            }
          } catch {
            isVisible = true;
          }
        }

        return {
          target: [...target] as [number, number, number],
          isVisible,
          distance: dist.direct,
          obstructions,
        };
      });
    };

    let sightLines = computeSightLines(observer, options.targets);

    if (viewer && Cesium) {
      try {
        const visLineColor = options.visibleLineColor
          ? Cesium.Color.fromCssColorString(options.visibleLineColor)
          : Cesium.Color.GREEN;
        const hidLineColor = options.hiddenLineColor
          ? Cesium.Color.fromCssColorString(options.hiddenLineColor)
          : Cesium.Color.RED;
        const obsPointColor = options.obstructionPointColor
          ? Cesium.Color.fromCssColorString(options.obstructionPointColor)
          : Cesium.Color.YELLOW;
        const lineWidth = options.lineWidth ?? 2;

        for (const sl of sightLines) {
          const color = sl.isVisible ? visLineColor : hidLineColor;
          const entity = viewer.entities.add({
            polyline: {
              positions: Cesium.Cartesian3.fromDegreesArrayHeights([
                observer[0], observer[1], observer[2],
                sl.target[0], sl.target[1], sl.target[2],
              ]),
              material: color,
              width: lineWidth,
            },
          });
          _entities.push(entity);

          for (const obs of sl.obstructions) {
            const ptEntity = viewer.entities.add({
              position: Cesium.Cartesian3.fromDegrees(obs.position[0], obs.position[1], obs.position[2]),
              point: {
                pixelSize: 8,
                color: obsPointColor,
              },
            });
            _entities.push(ptEntity);
          }
        }
      } catch {
      }
    }

    const result: MILineOfSightResult = {
      sightLines,
      remove() {
        if (_removed) return;
        _removed = true;
        if (viewer) {
          for (const entity of _entities) {
            try { viewer.entities.remove(entity); } catch {}
          }
        }
        _entities = [];
      },
      update(updateOpts: Partial<MILineOfSightOptions>) {
        result.remove();
        const merged = { ..._currentOptions, ...updateOpts };
        _currentOptions = merged;
        const newResult = MISpatialAnalysis3D.lineOfSight(scene, merged);
        Object.assign(result, newResult);
      },
      addTarget(target: [number, number, number]) {
        _currentOptions.targets = [..._currentOptions.targets, target];
        result.update({ targets: _currentOptions.targets });
      },
      removeTarget(index: number) {
        const targets = [..._currentOptions.targets];
        targets.splice(index, 1);
        _currentOptions.targets = targets;
        result.update({ targets: _currentOptions.targets });
      },
    };

    return result;
  }

  static measure(scene: any, options: MIMeasure3DOptions): MIMeasureHandle {
    const viewer = getViewer(scene);
    const unit = options.unit ?? 'meters';
    const areaUnit = options.areaUnit ?? 'square-meters';
    const volumeUnit = options.volumeUnit ?? 'cubic-meters';

    let _removed = false;
    let _entities: any[] = [];
    const _listeners: Map<string, Function[]> = new Map();

    const points: [number, number, number][] = [];

    const emitEvent = (event: string, data?: any) => {
      const cbs = _listeners.get(event);
      if (cbs) cbs.forEach(cb => cb(data));
    };

    const computeResult = (): MIMeasureResult => {
      const baseResult: MIMeasureResult = {
        type: options.type,
        value: 0,
        unit,
        points: [...points],
      };

      if (points.length < 2) return baseResult;

      switch (options.type) {
        case 'distance':
        case 'slope-distance': {
          let totalDirect = 0;
          let totalHorizontal = 0;
          let totalVertical = 0;
          for (let i = 1; i < points.length; i++) {
            const d = haversineDistance(
              points[i - 1][0], points[i - 1][1], points[i - 1][2],
              points[i][0], points[i][1], points[i][2]
            );
            totalDirect += d.direct;
            totalHorizontal += d.horizontal;
            totalVertical += Math.abs(d.vertical);
          }
          const slope = totalHorizontal > 0
            ? Math.atan2(totalVertical, totalHorizontal) / DEG_TO_RAD
            : 0;

          baseResult.value = convertDistance(totalDirect, unit);
          baseResult.directDistance = convertDistance(totalDirect, unit);
          baseResult.horizontalDistance = convertDistance(totalHorizontal, unit);
          baseResult.verticalDistance = convertDistance(totalVertical, unit);
          baseResult.surfaceDistance = convertDistance(totalDirect, unit);
          baseResult.slope = slope;
          break;
        }

        case 'height': {
          if (points.length >= 2) {
            const vDist = Math.abs(points[1][2] - points[0][2]);
            baseResult.value = convertDistance(vDist, unit);
            baseResult.verticalDistance = convertDistance(vDist, unit);
          }
          break;
        }

        case 'area': {
          if (points.length >= 3) {
            let areaM2 = 0;
            const n = points.length;
            for (let i = 0; i < n; i++) {
              const j = (i + 1) % n;
              const xi = points[i][0] * DEG_TO_RAD * EARTH_RADIUS_M * Math.cos(points[i][1] * DEG_TO_RAD);
              const yi = points[i][1] * DEG_TO_RAD * EARTH_RADIUS_M;
              const xj = points[j][0] * DEG_TO_RAD * EARTH_RADIUS_M * Math.cos(points[j][1] * DEG_TO_RAD);
              const yj = points[j][1] * DEG_TO_RAD * EARTH_RADIUS_M;
              areaM2 += xi * yj - xj * yi;
            }
            areaM2 = Math.abs(areaM2) / 2;

            let perimeter = 0;
            for (let i = 0; i < n; i++) {
              const j = (i + 1) % n;
              const d = haversineDistance(
                points[i][0], points[i][1], points[i][2],
                points[j][0], points[j][1], points[j][2]
              );
              perimeter += d.direct;
            }

            baseResult.value = convertArea(areaM2, areaUnit);
            baseResult.area = convertArea(areaM2, areaUnit);
            baseResult.unit = areaUnit;
            baseResult.perimeter = convertDistance(perimeter, unit);
          }
          break;
        }

        case 'volume': {
          if (points.length >= 3) {
            let baseArea = 0;
            const n = points.length;
            for (let i = 0; i < n; i++) {
              const j = (i + 1) % n;
              const xi = points[i][0] * DEG_TO_RAD * EARTH_RADIUS_M * Math.cos(points[i][1] * DEG_TO_RAD);
              const yi = points[i][1] * DEG_TO_RAD * EARTH_RADIUS_M;
              const xj = points[j][0] * DEG_TO_RAD * EARTH_RADIUS_M * Math.cos(points[j][1] * DEG_TO_RAD);
              const yj = points[j][1] * DEG_TO_RAD * EARTH_RADIUS_M;
              baseArea += xi * yj - xj * yi;
            }
            baseArea = Math.abs(baseArea) / 2;

            const elevations = points.map(p => p[2]);
            const minElev = Math.min(...elevations);
            const maxElev = Math.max(...elevations);
            const avgElev = elevations.reduce((sum, e) => sum + e, 0) / elevations.length;
            const heightDiff = maxElev - minElev;

            const totalVolume = baseArea * heightDiff;
            const cutVolume = baseArea * (maxElev - avgElev);
            const fillVolume = baseArea * (avgElev - minElev);

            baseResult.value = convertVolume(totalVolume, volumeUnit);
            baseResult.volume = convertVolume(totalVolume, volumeUnit);
            baseResult.cutVolume = convertVolume(cutVolume, volumeUnit);
            baseResult.fillVolume = convertVolume(fillVolume, volumeUnit);
            baseResult.unit = volumeUnit;
          }
          break;
        }

        case 'elevation-profile': {
          if (points.length >= 2) {
            const profile: { distance: number; elevation: number }[] = [];
            let cumulativeDist = 0;

            profile.push({ distance: 0, elevation: points[0][2] });

            for (let i = 1; i < points.length; i++) {
              const seg = haversineDistance(
                points[i - 1][0], points[i - 1][1], points[i - 1][2],
                points[i][0], points[i][1], points[i][2]
              );
              cumulativeDist += seg.horizontal;

              if (options.followTerrain && viewer && Cesium) {
                const numSamples = 10;
                const segLength = seg.horizontal;
                for (let s = 1; s <= numSamples; s++) {
                  const t = s / numSamples;
                  const sampleLon = points[i - 1][0] + (points[i][0] - points[i - 1][0]) * t;
                  const sampleLat = points[i - 1][1] + (points[i][1] - points[i - 1][1]) * t;
                  const terrainH = sampleTerrainHeight(viewer, sampleLon, sampleLat);
                  const dist = cumulativeDist - segLength + segLength * t;
                  profile.push({ distance: convertDistance(dist, unit), elevation: terrainH });
                }
              } else {
                profile.push({
                  distance: convertDistance(cumulativeDist, unit),
                  elevation: points[i][2],
                });
              }
            }

            baseResult.value = convertDistance(cumulativeDist, unit);
            baseResult.elevationProfile = profile;
            baseResult.surfaceDistance = convertDistance(cumulativeDist, unit);
          }
          break;
        }
      }

      return baseResult;
    };

    let currentResult = computeResult();

    const handle: MIMeasureHandle = {
      get result() {
        return currentResult;
      },
      on(event: string, callback: Function) {
        if (!_listeners.has(event)) _listeners.set(event, []);
        _listeners.get(event)!.push(callback);
      },
      clear() {
        points.length = 0;
        if (viewer) {
          for (const entity of _entities) {
            try { viewer.entities.remove(entity); } catch {}
          }
        }
        _entities = [];
        currentResult = computeResult();
        emitEvent('clear');
      },
      remove() {
        if (_removed) return;
        _removed = true;
        handle.clear();
        emitEvent('remove');
      },
    };

    if (viewer && Cesium && options.type !== 'elevation-profile') {
      try {
        const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        handler.setInputAction((click: any) => {
          if (_removed) {
            handler.destroy();
            return;
          }
          const cartesian = viewer.scene.pickPosition(click.position);
          if (!cartesian) return;
          const carto = Cesium.Cartographic.fromCartesian(cartesian);
          const pt: [number, number, number] = [
            Cesium.Math.toDegrees(carto.longitude),
            Cesium.Math.toDegrees(carto.latitude),
            carto.height,
          ];
          points.push(pt);
          currentResult = computeResult();
          emitEvent('point-added', { point: pt, result: currentResult });

          const entity = viewer.entities.add({
            position: cartesian,
            point: { pixelSize: 6, color: Cesium.Color.WHITE },
          });
          _entities.push(entity);

          if (points.length > 1) {
            const prev = points[points.length - 2];
            const lineEntity = viewer.entities.add({
              polyline: {
                positions: Cesium.Cartesian3.fromDegreesArrayHeights([
                  prev[0], prev[1], prev[2],
                  pt[0], pt[1], pt[2],
                ]),
                material: Cesium.Color.CYAN,
                width: 2,
              },
            });
            _entities.push(lineEntity);
          }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

        handler.setInputAction(() => {
          if (_removed) {
            handler.destroy();
            return;
          }
          currentResult = computeResult();
          emitEvent('complete', { result: currentResult });
          handler.destroy();
        }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      } catch {
      }
    }

    return handle;
  }

  static shadowAnalysis(scene: any, options: MIShadowAnalysisOptions): MIShadowAnalysisResult {
    const viewer = getViewer(scene);
    const date = options.date ?? new Date();
    const analysisType = options.type;
    let _removed = false;
    let _animationInterval: ReturnType<typeof setInterval> | null = null;

    let shadowCanvas: HTMLCanvasElement | undefined;
    let stats:
      | {
          totalArea: number;
          shadowedArea: number;
          litArea: number;
          shadowPercentage: number;
          hoursOfSunlight?: Map<string, number>;
          minimumSunlightHours?: number;
          maximumSunlightHours?: number;
          averageSunlightHours?: number;
        }
      | undefined;

    const outputType = options.outputType ?? 'both';
    const surfaceRes = options.surfaceResolution ?? 50;

    if (viewer && Cesium) {
      try {
        viewer.scene.globe.enableLighting = true;
        viewer.shadows = true;
        viewer.clock.currentTime = Cesium.JulianDate.fromDate(date);
      } catch {
      }
    }

    if (analysisType === 'instant') {
      const camera = scene?.getCamera?.() ?? { position: [0, 0, 0] };
      const lat = camera.position?.[1] ?? 0;
      const lon = camera.position?.[0] ?? 0;
      const sunPos = computeSunPosition(date, lat, lon);

      const totalArea = surfaceRes * surfaceRes;
      const shadowPercentage = sunPos.elevation > 0
        ? Math.max(0, 100 - sunPos.elevation * 1.2)
        : 100;
      const shadowedArea = totalArea * (shadowPercentage / 100);
      const litArea = totalArea - shadowedArea;

      if (outputType === 'visual' || outputType === 'both') {
        try {
          shadowCanvas = document.createElement('canvas');
          shadowCanvas.width = surfaceRes;
          shadowCanvas.height = surfaceRes;
          const ctx = shadowCanvas.getContext('2d');
          if (ctx) {
            const shadowAngle = sunPos.azimuth * DEG_TO_RAD;
            const shadowLength = sunPos.elevation > 0
              ? Math.tan((90 - sunPos.elevation) * DEG_TO_RAD) * 10
              : surfaceRes;

            ctx.fillStyle = '#ffff00';
            ctx.fillRect(0, 0, surfaceRes, surfaceRes);

            ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
            const cx = surfaceRes / 2;
            const cy = surfaceRes / 2;
            const sx = Math.sin(shadowAngle) * shadowLength;
            const sy = -Math.cos(shadowAngle) * shadowLength;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + sx - 5, cy + sy - 5);
            ctx.lineTo(cx + sx + 5, cy + sy + 5);
            ctx.closePath();
            ctx.fill();
          }
        } catch {
        }
      }

      if (outputType === 'statistics' || outputType === 'both') {
        stats = {
          totalArea,
          shadowedArea,
          litArea,
          shadowPercentage,
        };
      }
    } else if (analysisType === 'duration') {
      const timeRange = options.timeRange ?? {
        start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 6, 0),
        end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 18, 0),
      };
      const timeStep = options.timeStep ?? 60;
      const camera = scene?.getCamera?.() ?? { position: [0, 0, 0] };
      const lat = camera.position?.[1] ?? 0;
      const lon = camera.position?.[0] ?? 0;

      const hoursOfSunlight = new Map<string, number>();
      let totalSunlightHours = 0;
      let sampleCount = 0;

      const startTime = timeRange.start.getTime();
      const endTime = timeRange.end.getTime();
      const stepMs = timeStep * 60 * 1000;

      for (let t = startTime; t <= endTime; t += stepMs) {
        const sampleDate = new Date(t);
        const sunPos = computeSunPosition(sampleDate, lat, lon);
        const timeKey = sampleDate.toISOString();
        const sunlightHours = sunPos.elevation > 0 ? (timeStep / 60) : 0;
        hoursOfSunlight.set(timeKey, sunPos.elevation);
        totalSunlightHours += sunlightHours;
        sampleCount++;
      }

      const sunlightValues = Array.from(hoursOfSunlight.values()).filter(v => v > 0);
      const totalDuration = (endTime - startTime) / (3600 * 1000);

      stats = {
        totalArea: surfaceRes * surfaceRes,
        shadowedArea: 0,
        litArea: surfaceRes * surfaceRes,
        shadowPercentage: totalDuration > 0
          ? ((totalDuration - totalSunlightHours) / totalDuration) * 100
          : 0,
        hoursOfSunlight,
        minimumSunlightHours: sunlightValues.length > 0 ? Math.min(...sunlightValues) : 0,
        maximumSunlightHours: sunlightValues.length > 0 ? Math.max(...sunlightValues) : 0,
        averageSunlightHours: sampleCount > 0 ? totalSunlightHours / sampleCount : 0,
      };

      stats.shadowedArea = stats.totalArea * (stats.shadowPercentage / 100);
      stats.litArea = stats.totalArea - stats.shadowedArea;
    } else if (analysisType === 'animation') {
      const timeRange = options.timeRange ?? {
        start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 6, 0),
        end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 18, 0),
      };
      const timeStep = options.timeStep ?? 30;

      if (viewer && Cesium) {
        try {
          const startJd = Cesium.JulianDate.fromDate(timeRange.start);
          const endJd = Cesium.JulianDate.fromDate(timeRange.end);
          viewer.clock.startTime = startJd;
          viewer.clock.stopTime = endJd;
          viewer.clock.currentTime = startJd.clone();
          viewer.clock.clockRange = Cesium.ClockRange.LOOP_STOP;
          viewer.clock.multiplier = timeStep * 60;
          viewer.clock.shouldAnimate = true;
        } catch {
        }
      } else {
        let currentTime = timeRange.start.getTime();
        const stepMs = timeStep * 60 * 1000;
        _animationInterval = setInterval(() => {
          currentTime += stepMs;
          if (currentTime > timeRange.end.getTime()) {
            currentTime = timeRange.start.getTime();
          }
        }, 1000);
      }

      stats = {
        totalArea: surfaceRes * surfaceRes,
        shadowedArea: 0,
        litArea: surfaceRes * surfaceRes,
        shadowPercentage: 0,
      };
    }

    const animationHandle = analysisType === 'animation'
      ? {
          pause: () => {
            if (viewer && Cesium) {
              try { viewer.clock.shouldAnimate = false; } catch {}
            }
            if (_animationInterval) clearInterval(_animationInterval);
          },
          resume: () => {
            if (viewer && Cesium) {
              try { viewer.clock.shouldAnimate = true; } catch {}
            }
          },
          setSpeed: (multiplier: number) => {
            if (viewer && Cesium) {
              try { viewer.clock.multiplier = multiplier; } catch {}
            }
          },
        }
      : undefined;

    const result: MIShadowAnalysisResult = {
      type: analysisType,
      shadowMap: shadowCanvas,
      statistics: stats,
      animationHandle,
      remove() {
        if (_removed) return;
        _removed = true;
        if (_animationInterval) clearInterval(_animationInterval);
        if (viewer && Cesium) {
          try {
            viewer.clock.shouldAnimate = false;
            viewer.shadows = false;
          } catch {
          }
        }
      },
    };

    return result;
  }

  static crossSection(scene: any, options: MICrossSectionOptions): MICrossSectionResult {
    const viewer = getViewer(scene);
    const sampleRes = options.sampleResolution ?? 10;
    const surfaces = options.surfaces;
    let _removed = false;
    let _entities: any[] = [];
    let _currentLine = [...options.line];

    const computeProfiles = (
      line: [number, number, number][]
    ): {
      profiles: {
        surface: string;
        points: { distance: number; elevation: number; coordinate: [number, number, number] }[];
      }[];
      totalLength: number;
      elevationRange: [number, number];
    } => {
      const profiles: {
        surface: string;
        points: { distance: number; elevation: number; coordinate: [number, number, number] }[];
      }[] = [];

      let totalLength = 0;
      for (let i = 1; i < line.length; i++) {
        const d = haversineDistance(
          line[i - 1][0], line[i - 1][1], line[i - 1][2],
          line[i][0], line[i][1], line[i][2]
        );
        totalLength += d.horizontal;
      }

      let globalMin = Infinity;
      let globalMax = -Infinity;

      for (const surface of surfaces) {
        const surfacePoints: { distance: number; elevation: number; coordinate: [number, number, number] }[] = [];
        let cumulativeDist = 0;

        for (let segIdx = 0; segIdx < line.length - 1; segIdx++) {
          const p1 = line[segIdx];
          const p2 = line[segIdx + 1];
          const segDist = haversineDistance(
            p1[0], p1[1], p1[2],
            p2[0], p2[1], p2[2]
          );
          const segLength = segDist.horizontal;
          const numSamples = Math.max(2, Math.ceil(segLength / sampleRes));

          for (let s = 0; s <= numSamples; s++) {
            if (segIdx > 0 && s === 0) continue;

            const t = s / numSamples;
            const samplePos = interpolatePosition(p1, p2, t);
            const sampleDist = cumulativeDist + segLength * t;

            let elevation = samplePos[2];

            if (surface === 'terrain' && viewer && Cesium) {
              const terrainH = sampleTerrainHeight(viewer, samplePos[0], samplePos[1]);
              if (terrainH !== 0 || elevation === 0) {
                elevation = terrainH;
              }
            } else if (surface === '3dtiles' || surface === 'pointcloud') {
              if (viewer && Cesium) {
                try {
                  const cartesian = Cesium.Cartesian3.fromDegrees(
                    samplePos[0], samplePos[1], samplePos[2] + 1000
                  );
                  const ray = new Cesium.Ray(cartesian, Cesium.Cartesian3.negate(
                    Cesium.Cartesian3.UNIT_Z, new Cesium.Cartesian3()
                  ));
                  const result = viewer.scene.pickFromRay(ray);
                  if (result && result.position) {
                    const carto = Cesium.Cartographic.fromCartesian(result.position);
                    elevation = carto.height;
                  }
                } catch {
                }
              }
            }

            if (elevation < globalMin) globalMin = elevation;
            if (elevation > globalMax) globalMax = elevation;

            surfacePoints.push({
              distance: sampleDist,
              elevation,
              coordinate: [...samplePos] as [number, number, number],
            });
          }

          cumulativeDist += segLength;
        }

        profiles.push({ surface, points: surfacePoints });
      }

      if (globalMin === Infinity) globalMin = 0;
      if (globalMax === -Infinity) globalMax = 0;

      return {
        profiles,
        totalLength,
        elevationRange: [globalMin, globalMax],
      };
    };

    const computed = computeProfiles(_currentLine);

    let chart: HTMLCanvasElement | undefined;
    try {
      chart = document.createElement('canvas');
      chart.width = 600;
      chart.height = 300;
      const ctx = chart.getContext('2d');
      if (ctx && computed.profiles.length > 0) {
        const padding = 40;
        const w = chart.width - padding * 2;
        const h = chart.height - padding * 2;
        const [minElev, maxElev] = computed.elevationRange;
        const elevRange = maxElev - minElev || 1;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, chart.width, chart.height);

        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 0.5;
        for (let i = 0; i <= 5; i++) {
          const y = padding + (h / 5) * i;
          ctx.beginPath();
          ctx.moveTo(padding, y);
          ctx.lineTo(padding + w, y);
          ctx.stroke();
        }

        const colors = ['#3388ff', '#ff6633', '#33cc33'];
        computed.profiles.forEach((profile, idx) => {
          ctx.strokeStyle = colors[idx % colors.length];
          ctx.lineWidth = 2;
          ctx.beginPath();

          const totalLen = computed.totalLength || 1;
          profile.points.forEach((pt, i) => {
            const x = padding + (pt.distance / totalLen) * w;
            const y = padding + h - ((pt.elevation - minElev) / elevRange) * h;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });

          ctx.stroke();
        });

        ctx.fillStyle = '#000000';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('0', padding, padding + h + 15);
        ctx.fillText(
          `${Math.round(computed.totalLength)}m`,
          padding + w,
          padding + h + 15
        );
        ctx.textAlign = 'right';
        ctx.fillText(`${Math.round(minElev)}m`, padding - 5, padding + h);
        ctx.fillText(`${Math.round(maxElev)}m`, padding - 5, padding + 5);
      }
    } catch {
      chart = undefined;
    }

    if (viewer && Cesium) {
      try {
        const lineColor = options.lineColor
          ? Cesium.Color.fromCssColorString(options.lineColor)
          : Cesium.Color.CYAN;
        const positions: number[] = [];
        for (const pt of _currentLine) {
          positions.push(pt[0], pt[1], pt[2]);
        }
        const entity = viewer.entities.add({
          polyline: {
            positions: Cesium.Cartesian3.fromDegreesArrayHeights(positions),
            material: lineColor,
            width: 3,
          },
        });
        _entities.push(entity);

        for (const profile of computed.profiles) {
          const profilePositions: number[] = [];
          for (const pt of profile.points) {
            profilePositions.push(pt.coordinate[0], pt.coordinate[1], pt.elevation);
          }
          if (profilePositions.length >= 6) {
            const profileEntity = viewer.entities.add({
              polyline: {
                positions: Cesium.Cartesian3.fromDegreesArrayHeights(profilePositions),
                material: Cesium.Color.YELLOW.withAlpha(0.7),
                width: 2,
              },
            });
            _entities.push(profileEntity);
          }
        }
      } catch {
      }
    }

    const result: MICrossSectionResult = {
      profiles: computed.profiles,
      totalLength: computed.totalLength,
      elevationRange: computed.elevationRange,
      chart,
      remove() {
        if (_removed) return;
        _removed = true;
        if (viewer) {
          for (const entity of _entities) {
            try { viewer.entities.remove(entity); } catch {}
          }
        }
        _entities = [];
      },
      update(line: [number, number, number][]) {
        result.remove();
        _currentLine = [...line];
        _removed = false;
        const newResult = MISpatialAnalysis3D.crossSection(scene, { ...options, line });
        result.profiles = newResult.profiles;
        result.totalLength = newResult.totalLength;
        result.elevationRange = newResult.elevationRange;
        result.chart = newResult.chart;
        _entities = [];
      },
    };

    return result;
  }

  static async floodAnalysis(scene: any, options: MIFloodAnalysisOptions): Promise<MIFloodAnalysisResult & { floodGeoJSON?: any; elevationStats?: any; floodedPercentage?: number }> {
    const extent = options.extent || { west: 76, south: 12, east: 78, north: 14 };
    const waterLevel = options.waterLevel;

    const serverResult = await apiCall('/analysis/flood', {
      extent,
      waterLevel,
      resolution: 15,
    });

    let _removed = false;
    let _entities: any[] = [];
    const viewer = scene?.getViewer?.();
    const display = options.display || {};
    const fillColor = display.fillColor || 'rgba(0, 100, 255, 0.4)';

    if (viewer && Cesium) {
      try {
        const entity = viewer.entities.add({
          rectangle: {
            coordinates: Cesium.Rectangle.fromDegrees(extent.west, extent.south, extent.east, extent.north),
            material: Cesium.Color.fromCssColorString(fillColor),
            height: waterLevel,
          },
        });
        _entities.push(entity);
      } catch {}
    }

    const _listeners: Record<string, Function[]> = {};

    const result: MIFloodAnalysisResult & { floodGeoJSON?: any; elevationStats?: any; floodedPercentage?: number } = {
      floodExtent: serverResult.floodGeoJSON?.features || [],
      affectedArea: serverResult.floodedArea,
      maxDepth: serverResult.maxDepth,
      avgDepth: serverResult.avgDepth,
      volumeOfWater: serverResult.volumeOfWater,
      floodGeoJSON: serverResult.floodGeoJSON,
      elevationStats: serverResult.elevationStats,
      floodedPercentage: serverResult.floodedPercentage,
      depthMap: undefined,
      on(event: string, callback: Function) {
        if (!_listeners[event]) _listeners[event] = [];
        _listeners[event].push(callback);
      },
      async setWaterLevel(level: number) {
        const newResult = await apiCall('/analysis/flood', { extent, waterLevel: level, resolution: 15 });
        result.affectedArea = newResult.floodedArea;
        result.maxDepth = newResult.maxDepth;
        result.avgDepth = newResult.avgDepth;
        result.volumeOfWater = newResult.volumeOfWater;
        result.floodGeoJSON = newResult.floodGeoJSON;
        result.floodedPercentage = newResult.floodedPercentage;
        (_listeners['level-change'] || []).forEach(cb => cb(level, result));
      },
      remove() {
        if (_removed) return;
        _removed = true;
        if (viewer) {
          for (const e of _entities) {
            try { viewer.entities.remove(e); } catch {}
          }
        }
        _entities = [];
      },
    };

    return result;
  }

  static async volumeComparison(scene: any, options: MIVolumeComparisonOptions): Promise<MIVolumeComparisonResult & { differenceGeoJSON?: any }> {
    const extent = options.extent || { west: 76, south: 20, east: 80, north: 24 };

    const serverResult = await apiCall('/analysis/volume', {
      extent,
      resolution: Math.min(options.sampleResolution || 15, 20),
      modificationDepth: 5,
    });

    let _removed = false;
    return {
      cutVolume: serverResult.cutVolume,
      fillVolume: serverResult.fillVolume,
      netVolume: serverResult.netVolume,
      cutArea: serverResult.cutArea,
      fillArea: serverResult.fillArea,
      unchangedArea: serverResult.unchangedArea,
      maxCutDepth: serverResult.maxCutDepth,
      maxFillHeight: serverResult.maxFillHeight,
      differenceGeoJSON: serverResult.differenceGeoJSON,
      differenceMap: undefined,
      toggleView(_mode: 'original' | 'modified' | 'difference') {},
      remove() { _removed = true; },
    };
  }

  static async terrainProfile(scene: any, options: MITerrainProfileOptions): Promise<MITerrainProfileResult> {
    const path = Array.isArray(options.path)
      ? options.path as [number, number][]
      : (options.path as any)?.coordinates || [];

    const serverPath = path.map((p: [number, number]) => [p[1], p[0]]);
    const units = options.units === 'feet' ? 'imperial' : 'metric';

    const serverResult = await apiCall('/elevation/profile', {
      path: serverPath,
      numSamples: Math.max(20, Math.floor(path.length * 10)),
      units,
    });

    const profile = serverResult.profile.map((p: any) => ({
      distance: p.distance,
      elevation: p.elevation,
      slope: p.slope,
      aspect: 0,
    }));

    let _removed = false;
    return {
      profile,
      totalDistance: serverResult.totalDistance,
      maxElevation: serverResult.maxElevation,
      minElevation: serverResult.minElevation,
      totalAscent: serverResult.totalAscent,
      totalDescent: serverResult.totalDescent,
      averageSlope: serverResult.averageSlope,
      maxSlope: serverResult.maxSlope,
      comparativeProfiles: undefined,
      chartElement: document.createElement('div'),
      remove() { _removed = true; },
    };
  }

  static async bestPath(scene: any, options: MIBestPathOptions): Promise<MIBestPathResult> {
    const serverResult = await apiCall('/analysis/bestpath', {
      start: options.start,
      end: options.end,
      waypoints: options.waypoints,
      constraints: options.constraints,
      algorithm: options.algorithm,
    });

    let _removed = false;
    return {
      path: serverResult.path,
      totalDistance: serverResult.totalDistance,
      estimatedTraversalTime: serverResult.estimatedTraversalTime,
      totalAscent: serverResult.totalAscent,
      totalDescent: serverResult.totalDescent,
      maxSlope: serverResult.maxSlope,
      elevationProfile: serverResult.elevationProfile,
      pathGeometry: serverResult.pathGeometry,
      alternateRoutes: undefined,
      remove() { _removed = true; },
    };
  }

  static trajectory(scene: any, options: MITrajectoryOptions): MITrajectoryResult {
    const origin = options.origin;
    const launchAngle = (options.launchAngle * Math.PI) / 180;
    const azimuth = ((options.azimuth || 0) * Math.PI) / 180;
    const speed = options.speed;
    const gravity = options.environment?.gravity || 9.81;
    const timeStep = options.timeStep || 0.1;
    const drag = options.environment?.dragCoefficient || 0.47;
    const airDensity = options.environment?.airDensity || 1.225;

    const vx = speed * Math.cos(launchAngle) * Math.sin(azimuth);
    const vy = speed * Math.cos(launchAngle) * Math.cos(azimuth);
    const vz = speed * Math.sin(launchAngle);

    const path: [number, number, number][] = [origin];
    let x = 0, y = 0, z = origin[2] || 0;
    let cvx = vx, cvy = vy, cvz = vz;
    let maxAlt = z;
    let apexPoint: [number, number, number] = [...origin];
    let totalTime = 0;
    let impactPoint: [number, number, number] | undefined;

    const metersToDegreesLon = 1 / (111000 * Math.cos((origin[1] * Math.PI) / 180));
    const metersToDegreesLat = 1 / 111000;

    for (let step = 0; step < 10000; step++) {
      const v = Math.sqrt(cvx * cvx + cvy * cvy + cvz * cvz);
      const dragForce = 0.5 * airDensity * drag * 0.01 * v * v;
      const ax = v > 0 ? -dragForce * cvx / v : 0;
      const ay = v > 0 ? -dragForce * cvy / v : 0;
      const az = -gravity + (v > 0 ? -dragForce * cvz / v : 0);

      cvx += ax * timeStep;
      cvy += ay * timeStep;
      cvz += az * timeStep;
      x += cvx * timeStep;
      y += cvy * timeStep;
      z += cvz * timeStep;
      totalTime += timeStep;

      const lon = origin[0] + x * metersToDegreesLon;
      const lat = origin[1] + y * metersToDegreesLat;

      if (z > maxAlt) {
        maxAlt = z;
        apexPoint = [lon, lat, z];
      }

      path.push([lon, lat, z]);

      if (z <= 0 && step > 0) {
        impactPoint = [lon, lat, 0];
        break;
      }
    }

    const groundDist = Math.sqrt(x * x + y * y);
    const impactSpeed = Math.sqrt(cvx * cvx + cvy * cvy + cvz * cvz);
    const impactAngle = Math.atan2(Math.abs(cvz), Math.sqrt(cvx * cvx + cvy * cvy)) * (180 / Math.PI);

    let _removed = false;
    return {
      path,
      impactPoint,
      apexPoint,
      maxAltitude: maxAlt,
      totalDistance: groundDist,
      flightTime: totalTime,
      impactAngle,
      impactSpeed,
      deviationFromTarget: options.target
        ? Math.sqrt(
            Math.pow((impactPoint?.[0] || 0) - options.target[0], 2) +
            Math.pow((impactPoint?.[1] || 0) - options.target[1], 2)
          ) * 111000
        : undefined,
      pathGeometry: { type: 'LineString', coordinates: path },
      animate() {
        return { stop() {}, pause() {}, resume() {}, isPlaying: false, progress: 0 };
      },
      remove() { _removed = true; },
    };
  }

  static ballisticCalculation(options: MIBallisticOptions): MIBallisticResult {
    const obsLoc = Array.isArray(options.observerLocation) ? options.observerLocation : [0, 0];
    const tgtLoc = Array.isArray(options.targetLocation) ? options.targetLocation : [0, 0];

    const dx = (tgtLoc[0] - obsLoc[0]) * 111000 * Math.cos((obsLoc[1] * Math.PI) / 180);
    const dy = (tgtLoc[1] - obsLoc[1]) * 111000;
    const range = Math.sqrt(dx * dx + dy * dy);
    const azimuth = (Math.atan2(dx, dy) * 180) / Math.PI;
    if (azimuth < 0) azimuth + 360;

    const v0 = options.gunSpeed;
    const g = 9.81;
    const obsElev = options.terrainElevation?.observerElevation || 0;
    const tgtElev = options.terrainElevation?.targetElevation || 0;
    const elevDiff = tgtElev - obsElev;

    const sinTwoTheta = (range * g) / (v0 * v0);
    const canReach = Math.abs(sinTwoTheta) <= 1;

    let highTrajectory, lowTrajectory;

    if (canReach) {
      const theta1 = Math.asin(sinTwoTheta) / 2;
      const theta2 = (Math.PI - Math.asin(sinTwoTheta)) / 2;

      const computeTrajectory = (angle: number) => {
        const flightTime = (2 * v0 * Math.sin(angle)) / g;
        const maxOrdinate = (v0 * v0 * Math.sin(angle) * Math.sin(angle)) / (2 * g);
        const impactAngle = Math.atan2(v0 * Math.sin(angle), v0 * Math.cos(angle)) * (180 / Math.PI);
        const impactSpeed = v0;
        const trajectory: [number, number, number][] = [];
        const steps = 50;
        const metersToDegreesLon = 1 / (111000 * Math.cos((obsLoc[1] * Math.PI) / 180));
        const metersToDegreesLat = 1 / 111000;
        const dirX = dx / range;
        const dirY = dy / range;

        for (let i = 0; i <= steps; i++) {
          const t = (i / steps) * flightTime;
          const horiz = v0 * Math.cos(angle) * t;
          const vert = v0 * Math.sin(angle) * t - 0.5 * g * t * t + obsElev;
          const x = dirX * horiz;
          const y = dirY * horiz;
          trajectory.push([
            obsLoc[0] + x * metersToDegreesLon,
            obsLoc[1] + y * metersToDegreesLat,
            Math.max(0, vert),
          ]);
        }

        return {
          launchAngle: (angle * 180) / Math.PI,
          flightTime,
          maxOrdinate,
          trajectory,
          impactAngle,
          impactSpeed,
        };
      };

      if (options.options?.computeLowTrajectory !== false) {
        lowTrajectory = computeTrajectory(theta1);
      }
      if (options.options?.computeHighTrajectory !== false) {
        highTrajectory = computeTrajectory(theta2);
      }
    }

    return {
      azimuth: azimuth < 0 ? azimuth + 360 : azimuth,
      solutions: { highTrajectory, lowTrajectory },
      range,
      terrainMasked: false,
      maskingPoint: undefined,
      visualize(_scene: any) {},
    };
  }

  static async mobilityAnalysis(scene: any, options: MIMobilityOptions): Promise<MIMobilityResult & { mobilityGeoJSON?: any }> {
    const extent = options.extent || { west: 76, south: 20, east: 80, north: 24 };

    const serverResult = await apiCall('/analysis/mobility', {
      extent,
      vehicleProfile: options.vehicleProfile,
      resolution: Math.min(options.resolution || 15, 20),
    });

    let _removed = false;
    return {
      grid: new Float32Array(0),
      goArea: serverResult.goArea,
      slowGoArea: serverResult.slowGoArea,
      noGoArea: serverResult.noGoArea,
      coveragePercent: {
        go: serverResult.goPercentage,
        slowGo: serverResult.slowGoPercentage,
        noGo: serverResult.noGoPercentage,
      },
      mobilityGeoJSON: serverResult.mobilityGeoJSON,
      rasterLayer: null,
      remove() { _removed = true; },
    };
  }
}

export interface MIFloodAnalysisOptions {
  extent?: any;
  waterLevel: number;
  waterLevelRange?: { min: number; max: number; step: number };
  source?: {
    demLayer?: string;
    precipitationMm?: number;
    riverFlowRate?: number;
    drainageCoefficient?: number;
  };
  display?: {
    fillColor?: string;
    boundaryColor?: string;
    showDepthGradient?: boolean;
    showFloodExtentPolygon?: boolean;
    transparency?: number;
  };
}

export interface MIFloodAnalysisResult {
  floodExtent: any[];
  affectedArea: number;
  maxDepth: number;
  avgDepth: number;
  volumeOfWater: number;
  depthMap?: HTMLCanvasElement;
  on(event: string, callback: Function): void;
  setWaterLevel(level: number): void;
  remove(): void;
}

export interface MIVolumeComparisonOptions {
  preModificationDEM: string | any;
  postModificationDEM: string | any;
  extent?: any;
  sampleResolution?: number;
  display?: {
    cutColor?: string;
    fillColor?: string;
    showDifferenceMap?: boolean;
    showNumericalSummary?: boolean;
  };
}

export interface MIVolumeComparisonResult {
  cutVolume: number;
  fillVolume: number;
  netVolume: number;
  cutArea: number;
  fillArea: number;
  unchangedArea: number;
  maxCutDepth: number;
  maxFillHeight: number;
  differenceMap?: HTMLCanvasElement;
  toggleView(mode: 'original' | 'modified' | 'difference'): void;
  remove(): void;
}

export interface MITerrainProfileOptions {
  path: [number, number][] | any;
  interactive?: boolean;
  demSources?: any[];
  sampleInterval?: number;
  showSlopeColoring?: boolean;
  showGradient?: boolean;
  units?: 'meters' | 'feet';
}

export interface MITerrainProfileResult {
  profile: { distance: number; elevation: number; slope: number; aspect: number }[];
  totalDistance: number;
  maxElevation: number;
  minElevation: number;
  totalAscent: number;
  totalDescent: number;
  averageSlope: number;
  maxSlope: number;
  comparativeProfiles?: any;
  chartElement: HTMLElement;
  remove(): void;
}

export interface MIBestPathOptions {
  start: [number, number];
  end: [number, number];
  waypoints?: [number, number][];
  constraints: {
    maxClimbSlope?: number;
    maxDescentSlope?: number;
    avoidWater?: boolean;
    avoidForest?: boolean;
    maxElevation?: number;
    minElevation?: number;
    vehicleType?: 'foot' | 'wheeled' | 'tracked' | 'custom';
    customTrafficability?: (slope: number, elevation: number, landcover: string) => number;
  };
  algorithm?: 'a-star' | 'dijkstra' | 'rrt';
  searchRadius?: number;
  sampleResolution?: number;
  display?: {
    pathColor?: string;
    elevationColoring?: boolean;
    showElevationProfile?: boolean;
    showConstraintViolations?: boolean;
  };
}

export interface MIBestPathResult {
  path: [number, number, number][];
  totalDistance: number;
  estimatedTraversalTime: number;
  totalAscent: number;
  totalDescent: number;
  maxSlope: number;
  elevationProfile: { distance: number; elevation: number; slope: number }[];
  pathGeometry: GeoJSON.LineString;
  alternateRoutes?: MIBestPathResult[];
  remove(): void;
}

export interface MITrajectoryOptions {
  origin: [number, number, number];
  target?: [number, number, number];
  launchAngle: number;
  azimuth: number;
  speed: number;
  objectType?: 'projectile' | 'missile' | 'uav' | 'custom';
  environment?: {
    gravity?: number;
    windSpeed?: number;
    windDirection?: number;
    airDensity?: number;
    dragCoefficient?: number;
  };
  terrainInteraction?: boolean;
  timeStep?: number;
  display?: {
    pathColor?: string;
    showKeyPoints?: boolean;
    showTerrainProfile?: boolean;
    animate?: boolean;
    animationSpeed?: number;
  };
}

export interface MITrajectoryResult {
  path: [number, number, number][];
  impactPoint?: [number, number, number];
  apexPoint: [number, number, number];
  maxAltitude: number;
  totalDistance: number;
  flightTime: number;
  impactAngle?: number;
  impactSpeed?: number;
  deviationFromTarget?: number;
  pathGeometry: GeoJSON.LineString;
  animate(): any;
  remove(): void;
}

export interface MIBallisticOptions {
  observerLocation: [number, number] | string;
  targetLocation: [number, number] | string;
  gunSpeed: number;
  weaponType?: string;
  environment?: {
    windSpeed?: number;
    windDirection?: number;
    temperature?: number;
    pressure?: number;
    humidity?: number;
  };
  terrainElevation?: {
    observerElevation?: number;
    targetElevation?: number;
  };
  options?: {
    computeHighTrajectory?: boolean;
    computeLowTrajectory?: boolean;
    includeTerrainMask?: boolean;
  };
}

export interface MIBallisticResult {
  azimuth: number;
  solutions: {
    highTrajectory?: {
      launchAngle: number;
      flightTime: number;
      maxOrdinate: number;
      trajectory: [number, number, number][];
      impactAngle: number;
      impactSpeed: number;
    };
    lowTrajectory?: {
      launchAngle: number;
      flightTime: number;
      maxOrdinate: number;
      trajectory: [number, number, number][];
      impactAngle: number;
      impactSpeed: number;
    };
  };
  range: number;
  terrainMasked: boolean;
  maskingPoint?: [number, number, number];
  visualize(scene: any): void;
}

export interface MIMobilityOptions {
  extent: any;
  vehicleProfile: 'foot' | 'wheeled-light' | 'wheeled-heavy' | 'tracked' | 'custom';
  customProfile?: {
    maxSlope: number;
    maxSideSlope: number;
    minTurningRadius: number;
    waterFordingDepth: number;
    soilBearingRequired: number;
  };
  layers?: {
    slopeWeight?: number;
    landcoverWeight?: number;
    soilWeight?: number;
    waterWeight?: number;
  };
  resolution?: number;
  display?: {
    colorRamp?: string[];
    showLegend?: boolean;
    transparency?: number;
  };
}

export interface MIMobilityResult {
  grid: Float32Array;
  goArea: number;
  slowGoArea: number;
  noGoArea: number;
  coveragePercent: { go: number; slowGo: number; noGo: number };
  rasterLayer: any;
  remove(): void;
}
